// SPDX-License-Identifier: BUSL-1.1

/**
 * Quick Actions 2x2 grid for dashboard — navigation shortcuts + cache invalidate.
 *
 */

import { useState } from 'react';
import type * as React from 'react';
import { Link } from 'react-router';
import { Plus, Flag, Bot, Trash2, type LucideIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

type TileColor = 'blue' | 'purple' | 'green' | 'red';

const COLOR_CLASSES: Record<TileColor, string> = {
  blue: 'bg-blue-950/40 text-blue-400',
  purple: 'bg-purple-950/40 text-purple-400',
  green: 'bg-green-950/40 text-green-400',
  red: 'bg-red-950/40 text-red-400',
};

interface ActionTileProps {
  icon: LucideIcon;
  label: string;
  color: TileColor;
  to: string;
}

function ActionTile({
  icon: Icon,
  label,
  color,
  to,
}: ActionTileProps): React.ReactElement {
  return (
    <Link
      to={to}
      className="group flex items-start gap-3 rounded-lg border border-neutral-800 bg-neutral-900 p-3 transition hover:-translate-y-px hover:border-neutral-700"
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-md ${COLOR_CLASSES[color]}`}
      >
        <Icon className="size-4" />
      </div>
      <span className="text-sm font-medium text-neutral-200">{label}</span>
    </Link>
  );
}

function InvalidateCacheTile(): React.ReactElement {
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch('/cache/invalidate', {
        method: 'POST',
        body: JSON.stringify({ pattern: '*' }),
      }),
    onSuccess: () => {
      showSuccess('All cache entries invalidated');
      setOpen(false);
    },
    onError: showApiError,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            variant="outline"
            className="group h-auto justify-start gap-3 rounded-lg border-neutral-800 bg-neutral-900 p-3 text-left transition hover:-translate-y-px hover:border-red-900/60 hover:bg-red-950/10"
          />
        }
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-red-950/40 text-red-400">
          <Trash2 className="size-4" />
        </div>
        <span className="text-sm font-medium text-neutral-200">
          Invalidate Cache
        </span>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invalidate all cache entries?</DialogTitle>
          <DialogDescription>
            This flushes every key in the cache and forces a cold read on the
            next request. This is a destructive operation and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Invalidating...' : 'Invalidate all'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickActionsCard(): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ActionTile
            icon={Plus}
            label="New Config Draft"
            color="blue"
            to="/configs?new=true"
          />
          <ActionTile
            icon={Flag}
            label="Create Flag"
            color="purple"
            to="/flags?new=true"
          />
          <ActionTile
            icon={Bot}
            label="View Agents"
            color="green"
            to="/agents"
          />
          <InvalidateCacheTile />
        </div>
      </CardContent>
    </Card>
  );
}
